import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getRecipeById,
  deleteRecipe,
  uploadRecipePhoto,
  setCoverPhoto,
  deleteRecipePhoto,
  emailRecipe,
  setStarterTemplate,
  setWelcomeRecipe,
  CATEGORIES,
} from '../../services/recipeService';

const RecipeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');

  const fetchRecipe = async () => {
    try {
      const data = await getRecipeById(id);
      setRecipe(data.recipe);
    } catch (err) {
      setError(err.response?.data?.message || 'Recipe not found');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Any group member can edit or delete a shared recipe
  const canEdit = !!recipe;

  const handlePrint = () => {
    window.print();
  };

  const [templateSaving, setTemplateSaving] = useState(false);
  const handleToggleTemplate = async () => {
    setTemplateSaving(true);
    try {
      const data = await setStarterTemplate(id, !recipe.is_starter_template);
      setRecipe((prev) => ({ ...prev, is_starter_template: data.isStarterTemplate }));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update starter set');
    } finally {
      setTemplateSaving(false);
    }
  };

  const [welcomeSaving, setWelcomeSaving] = useState(false);
  const handleToggleWelcome = async () => {
    if (!recipe.is_welcome_recipe) {
      const confirmed = window.confirm(
        'This will replace whichever recipe is currently the welcome recipe (if any). Every brand-new cookbook will get this one instead, going forward. Continue?'
      );
      if (!confirmed) return;
    }
    setWelcomeSaving(true);
    try {
      const data = await setWelcomeRecipe(id, !recipe.is_welcome_recipe);
      setRecipe((prev) => ({ ...prev, is_welcome_recipe: data.isWelcomeRecipe }));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update welcome recipe');
    } finally {
      setWelcomeSaving(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailTo.trim()) return;
    setEmailSending(true);
    setEmailMsg('');
    try {
      await emailRecipe(id, emailTo.trim());
      setEmailMsg(`Sent to ${emailTo.trim()}!`);
      setEmailTo('');
    } catch (err) {
      setEmailMsg(err.response?.data?.message || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${recipe.title}"? This can't be undone.`)) return;
    try {
      await deleteRecipe(id);
      navigate('/recipes');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete recipe');
    }
  };

  const handleAddPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadRecipePhoto(id, file);
      fetchRecipe();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSetCover = async (photoId) => {
    try {
      await setCoverPhoto(id, photoId);
      fetchRecipe();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to set cover photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await deleteRecipePhoto(id, photoId);
      fetchRecipe();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete photo');
    }
  };

  if (loading) return <p style={styles.status}>Loading recipe...</p>;
  if (error) return <p style={styles.error}>{error}</p>;
  if (!recipe) return null;

  const categoryLabels = (recipe.categories || [])
    .map((cat) => CATEGORIES.find((c) => c.value === cat)?.label || cat);

  return (
    <div style={styles.container}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .recipe-print-area, .recipe-print-area * { visibility: visible; }
          .recipe-print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
        }
      `}</style>

      <button onClick={() => navigate('/recipes')} style={styles.backBtn} className="no-print">← Back to Recipes</button>

      <div style={styles.card} className="recipe-print-area">
        <div style={styles.headerRow}>
          <div>
            <h2 style={styles.title}>{recipe.title}</h2>
            <div style={styles.meta}>
              {categoryLabels.map((label) => (
                <span key={label} style={styles.badge}>{label}</span>
              ))}
              {recipe.prep_time && <span>Prep: {recipe.prep_time} min</span>}
              {recipe.cook_time && <span>Cook: {recipe.cook_time} min</span>}
              {recipe.servings && <span>Serves: {recipe.servings}</span>}
            </div>
          </div>
          <div style={styles.headerActions} className="no-print">
            <button onClick={handlePrint} style={styles.utilityBtn}>🖨️ Print</button>
            <button onClick={() => setShowEmailForm((v) => !v)} style={styles.utilityBtn}>✉️ Email</button>
            {user?.is_admin && (
              <button
                onClick={handleToggleTemplate}
                style={recipe.is_starter_template ? styles.templateBtnActive : styles.utilityBtn}
                disabled={templateSaving}
              >
                {templateSaving ? '...' : recipe.is_starter_template ? '⭐ In Starter Set' : '☆ Include in Starter Set'}
              </button>
            )}
            {user?.is_admin && (
              <button
                onClick={handleToggleWelcome}
                style={recipe.is_welcome_recipe ? styles.templateBtnActive : styles.utilityBtn}
                disabled={welcomeSaving}
              >
                {welcomeSaving ? '...' : recipe.is_welcome_recipe ? '🎉 Welcome Recipe' : 'Set as Welcome Recipe'}
              </button>
            )}
            {canEdit && (
              <>
                <button onClick={() => navigate(`/recipes/${id}/edit`)} style={styles.editBtn}>Edit</button>
                <button onClick={handleDelete} style={styles.deleteBtn}>Delete</button>
              </>
            )}
          </div>
        </div>

        {showEmailForm && (
          <form onSubmit={handleSendEmail} style={styles.emailForm} className="no-print">
            <input
              type="email"
              placeholder="recipient@example.com"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              style={styles.emailInput}
              disabled={emailSending}
            />
            <button type="submit" style={styles.emailSendBtn} disabled={emailSending}>
              {emailSending ? 'Sending...' : 'Send'}
            </button>
            {emailMsg && <span style={styles.emailMsg}>{emailMsg}</span>}
          </form>
        )}

        <div style={styles.photoSection}>
          {recipe.photos.length > 0 ? (
            <div style={styles.photoGrid}>
              {recipe.photos.map((photo) => (
                <div key={photo.id} style={styles.photoWrap}>
                  <img src={photo.photo_url} alt={recipe.title} style={styles.photo} />
                  {photo.is_cover && <span style={styles.coverTag}>Cover</span>}
                  {canEdit && (
                    <div style={styles.photoActions} className="no-print">
                      {!photo.is_cover && (
                        <button onClick={() => handleSetCover(photo.id)} style={styles.photoActionBtn}>Set as cover</button>
                      )}
                      <button onClick={() => handleDeletePhoto(photo.id)} style={styles.photoActionBtnDanger}>Delete</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={styles.noPhotos}>No photos yet</p>
          )}
          {canEdit && (
            <label style={styles.uploadLabel} className="no-print">
              {uploading ? 'Uploading...' : '+ Add Photo'}
              <input type="file" accept="image/*" onChange={handleAddPhoto} style={{ display: 'none' }} disabled={uploading} />
            </label>
          )}
        </div>

        <div style={styles.contentGrid}>
          <div>
            <h3 style={styles.sectionTitle}>Ingredients</h3>
            {recipe.ingredients.length === 0 ? (
              <p style={styles.empty}>No ingredients listed</p>
            ) : (
              <ul style={styles.ingredientList}>
                {recipe.ingredients.map((ing) => (
                  <li key={ing.id} style={styles.ingredientItem}>
                    <strong>{ing.amount} {ing.unit}</strong> {ing.name}
                    {ing.notes && <span style={styles.ingNotes}> ({ing.notes})</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h3 style={styles.sectionTitle}>Instructions</h3>
            {recipe.instructions.length === 0 ? (
              <p style={styles.empty}>No instructions listed</p>
            ) : (
              <ol style={styles.stepList}>
                {recipe.instructions.map((step) => (
                  <li key={step.id} style={styles.stepItem}>{step.instruction}</li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {recipe.notes && (
          <div style={styles.notesSection}>
            <h3 style={styles.sectionTitle}>Notes</h3>
            <p style={styles.notesText}>{recipe.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { maxWidth: '900px', margin: '0 auto' },
  backBtn: { background: 'none', border: 'none', color: '#e67e22', cursor: 'pointer', fontSize: '14px', marginBottom: '16px', padding: 0 },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  title: { color: '#333', margin: '0 0 10px 0' },
  meta: { display: 'flex', gap: '14px', alignItems: 'center', fontSize: '13px', color: '#888', flexWrap: 'wrap' },
  badge: { backgroundColor: '#fdf0e3', color: '#e67e22', padding: '4px 10px', borderRadius: '20px', fontWeight: '600' },
  headerActions: { display: 'flex', gap: '8px' },
  utilityBtn: { padding: '8px 16px', backgroundColor: '#f5f5f5', color: '#555', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
  templateBtnActive: { padding: '8px 16px', backgroundColor: '#fdf0e3', color: '#e67e22', border: '1px solid #e67e22', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
  emailForm: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', padding: '12px', backgroundColor: '#fdf0e3', borderRadius: '8px' },
  emailInput: { flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' },
  emailSendBtn: { padding: '8px 16px', backgroundColor: '#e67e22', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' },
  emailMsg: { fontSize: '13px', color: '#555' },
  editBtn: { padding: '8px 16px', backgroundColor: '#e67e22', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' },
  deleteBtn: { padding: '8px 16px', backgroundColor: 'transparent', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: '8px', cursor: 'pointer' },
  photoSection: { marginBottom: '28px' },
  photoGrid: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  photoWrap: { position: 'relative', width: '140px' },
  photo: { width: '140px', height: '140px', objectFit: 'cover', borderRadius: '8px' },
  coverTag: { position: 'absolute', top: '6px', left: '6px', backgroundColor: '#e67e22', color: 'white', fontSize: '10px', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' },
  photoActions: { display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' },
  photoActionBtn: { fontSize: '11px', padding: '4px', background: '#f5f5f5', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#666' },
  photoActionBtnDanger: { fontSize: '11px', padding: '4px', background: '#fdecea', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#e74c3c' },
  noPhotos: { color: '#999', fontSize: '14px' },
  uploadLabel: { display: 'inline-block', padding: '8px 16px', border: '1px dashed #e67e22', color: '#e67e22', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  contentGrid: { display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '32px' },
  notesSection: { marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #f0f0f0' },
  notesText: { color: '#555', fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  sectionTitle: { color: '#333', fontSize: '17px', marginBottom: '12px', borderBottom: '2px solid #fdf0e3', paddingBottom: '8px' },
  ingredientList: { listStyle: 'none', padding: 0, margin: 0 },
  ingredientItem: { padding: '8px 0', borderBottom: '1px solid #f5f5f5', color: '#444', fontSize: '14px' },
  ingNotes: { color: '#999', fontStyle: 'italic' },
  stepList: { paddingLeft: '20px', margin: 0 },
  stepItem: { padding: '8px 0', color: '#444', fontSize: '14px', lineHeight: '1.5' },
  empty: { color: '#999', fontSize: '14px' },
  status: { textAlign: 'center', color: '#999' },
  error: { textAlign: 'center', color: '#e74c3c' },
};

export default RecipeDetail;
